import { sum, uniqBy } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Total, TotalPrice } from 'core/elements';
import { NumberHelper } from 'core/helpers';
import { useAgGridClientTableContext } from 'core/states';
import { IPartInventoryItem } from 'parts/types';

import { getLocationKey } from './utils';

const STATUS_PANEL_CLASS = 'ag-status-name-value ag-status-panel pf-bold-totals';

const formatTotal = (records: IPartInventoryItem[], field: keyof IPartInventoryItem) => {
  const total = sum(records.map((record) => Number(record[field]) || 0));

  if (!total) {
    return undefined;
  }

  return NumberHelper.format({ amount: total });
};

interface IProps {
  isAlternateVersionEnabled?: boolean;
}

export const TotalsStatusBar = ({ isAlternateVersionEnabled }: IProps) => {
  const { t } = useTranslation();

  const { rows } = useAgGridClientTableContext();

  // BE returns one record per (location, reel) with location-level fields duplicated on
  // every reel row for the same location. Dedupe by the same key the grid groups on so
  // multi-reel locations aren't counted N times in the footer totals.
  const totals = useMemo(() => {
    const records = uniqBy(rows as IPartInventoryItem[], getLocationKey);

    const standardCost = isAlternateVersionEnabled
      ? undefined
      : sum(records.map((r) => Number(r.standardCost) || 0)) || undefined;

    return {
      quantityAvailable: formatTotal(records, 'quantityAvailable'),
      quantityAllocated: formatTotal(records, 'quantityAllocated'),
      quantityOnhand: formatTotal(records, 'quantityOnhand'),
      quantityBackorder: formatTotal(records, 'quantityBackorder'),
      quantityOnorder: formatTotal(records, 'quantityOnorder'),
      standardCost,
    };
  }, [rows, isAlternateVersionEnabled]);

  return (
    <>
      <Total
        label={t('headerAvailable')}
        value={totals.quantityAvailable}
        options={{ classNames: STATUS_PANEL_CLASS }}
      />
      <Total
        label={t('headerAllocated')}
        value={totals.quantityAllocated}
        options={{ classNames: STATUS_PANEL_CLASS }}
      />
      <Total
        label={t('headerOnHand')}
        value={totals.quantityOnhand}
        options={{ classNames: STATUS_PANEL_CLASS }}
      />
      <Total
        label={t('headerBackOrdered')}
        value={totals.quantityBackorder}
        options={{ classNames: STATUS_PANEL_CLASS }}
      />
      <Total
        label={t('headerOnOrder')}
        value={totals.quantityOnorder}
        options={{ classNames: STATUS_PANEL_CLASS }}
      />

      {!isAlternateVersionEnabled && (
        <TotalPrice
          label={t('headerStandardCost')}
          value={totals.standardCost}
          options={{ classNames: STATUS_PANEL_CLASS }}
        />
      )}
    </>
  );
};
